// Adapté de ScanGabon/apps/capture/src/securite/chiffrement.ts, lecture du 24 octobre 2026.
// Reprise locale pour CollectivitésConnect ; aucun service distant ni donnée source.
// Primitives de chiffrement local (WebCrypto uniquement, aucun algorithme maison).
//
// Schema :
// - une cle de donnees AES-GCM 256 bits est generee par WebCrypto pour chaque espace utilisateur ;
// - elle est enveloppee (AES-KW) par une cle derivee du code PIN local (PBKDF2-SHA256, sel aleatoire,
//   au moins 600 000 tours) ;
// - une fois deverrouillee, la cle de donnees est non extractible et ne vit qu'en memoire ;
// - chaque enregistrement est chiffre avec un nonce aleatoire de 96 bits qui lui est propre, et
//   l'identifiant de l'enregistrement sert de donnee authentifiee (un chiffre deplace est refuse).
export const ITERATIONS_PBKDF2 = 600_000;
import { octetsDepuisTexte, tamponIsole } from './octets';

export const LONGUEUR_NONCE = 12;
export const LONGUEUR_SEL = 16;

export class PinIncorrect extends Error {
  constructor() {
    super('Code PIN incorrect');
    this.name = 'PinIncorrect';
  }
}

export interface CleEnveloppee {
  sel: Uint8Array;
  iterations: number;
  enveloppe: Uint8Array;
}

export interface Chiffre {
  nonce: Uint8Array;
  donnees: Uint8Array;
}

async function deriverCleEnveloppe(
  pin: string,
  sel: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  if (iterations < ITERATIONS_PBKDF2) {
    throw new Error(`Nombre de tours PBKDF2 insuffisant (minimum ${ITERATIONS_PBKDF2})`);
  }
  if (!Number.isInteger(iterations) || iterations > 2_000_000 || sel.byteLength !== LONGUEUR_SEL)
    throw new Error('Paramètres de chiffrement invalides');
  const materiau = await crypto.subtle.importKey(
    'raw',
    tamponIsole(octetsDepuisTexte(pin)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: tamponIsole(sel), iterations },
    materiau,
    { name: 'AES-KW', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

// Cree une cle de donnees neuve et la renvoie enveloppee par le PIN, ainsi que sa forme utilisable
// (non extractible). La forme extractible n'existe que le temps de l'enveloppement.
export async function creerCleDonnees(
  pin: string,
  iterations: number = ITERATIONS_PBKDF2,
): Promise<{ cle: CryptoKey; enveloppee: CleEnveloppee }> {
  const sel = crypto.getRandomValues(new Uint8Array(LONGUEUR_SEL));
  const cleEnveloppe = await deriverCleEnveloppe(pin, sel, iterations);
  const temporaire = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const enveloppe = new Uint8Array(
    await crypto.subtle.wrapKey('raw', temporaire, cleEnveloppe, 'AES-KW'),
  );
  const enveloppee: CleEnveloppee = { sel, iterations, enveloppe };
  const cle = await deverrouillerCleDonnees(pin, enveloppee);
  return { cle, enveloppee };
}

// Deverrouille la cle de donnees. Un mauvais PIN fait echouer le controle d'integrite d'AES-KW.
export async function deverrouillerCleDonnees(
  pin: string,
  enveloppee: CleEnveloppee,
): Promise<CryptoKey> {
  if (enveloppee.enveloppe.byteLength !== 40) throw new Error('Clé enveloppée invalide');
  const cleEnveloppe = await deriverCleEnveloppe(pin, enveloppee.sel, enveloppee.iterations);
  try {
    return await crypto.subtle.unwrapKey(
      'raw',
      tamponIsole(enveloppee.enveloppe),
      cleEnveloppe,
      'AES-KW',
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  } catch {
    throw new PinIncorrect();
  }
}

export async function chiffrerOctets(
  cle: CryptoKey,
  clair: Uint8Array,
  contexte: string,
): Promise<Chiffre> {
  const nonce = crypto.getRandomValues(new Uint8Array(LONGUEUR_NONCE));
  const donnees = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: tamponIsole(nonce),
      additionalData: tamponIsole(octetsDepuisTexte(contexte)),
    },
    cle,
    tamponIsole(clair),
  );
  return { nonce, donnees: new Uint8Array(donnees) };
}

export async function dechiffrerOctets(
  cle: CryptoKey,
  chiffre: Chiffre,
  contexte: string,
): Promise<Uint8Array> {
  const clair = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: tamponIsole(chiffre.nonce),
      additionalData: tamponIsole(octetsDepuisTexte(contexte)),
    },
    cle,
    tamponIsole(chiffre.donnees),
  );
  return new Uint8Array(clair);
}

// Cle propre a l'appareil, non extractible, conservee telle quelle dans IndexedDB (le navigateur
// serialise l'objet CryptoKey sans exposer son contenu au code JavaScript). Sert a proteger le jeton
// d'appareil, necessaire avant l'ouverture d'un espace utilisateur.
export async function creerCleAppareil(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
